/* DEINSPAR | Editorial Poster + Frame row, presentation only.
 * Keeps the existing size/frame buttons and their event handlers intact.
 * Frame pictures come from the Shopify frame product featured image.
 * Frame 'none' intentionally renders an empty #FAFAFA square.
 */
(() => {
  'use strict';
  /* Shared right drawer for desktop and mobile. The ORIGINAL Shopify option
     buttons retain their click handlers, prices, variants and set-frame logic.
     On mobile, native bottom sheets still own the options, but are hidden
     visually while this animated, full-width drawer is active. */
  function initResponsiveDrawer(picker, getFramePreviewUrl) {
    if (picker.dataset.dsResponsiveDrawerReady === 'true') return;
    const sizePicker = picker.querySelector('[data-custom-size-picker]');
    const framePicker = picker.querySelector('[data-custom-frame-picker]');
    const sizeTrigger = picker.querySelector('[data-custom-size-trigger]');
    const frameTrigger = picker.querySelector('[data-custom-frame-trigger]');
    if (!sizePicker || !framePicker || !sizeTrigger || !frameTrigger) return;
    picker.dataset.dsResponsiveDrawerReady = 'true';
    // main-product.js still opens its native (hidden) sheet in the background.
    // Its legacy body scroll lock must not move Safari's sticky header while
    // the editorial drawer slides above the entire page.
    document.body.classList.add('ds-editorial-drawer-ready');

    const mobile = window.matchMedia('(max-width: 768px)');
    const root = document.createElement('div');
    root.className = 'ds-editorial-drawer';
    root.hidden = true;
    root.innerHTML = '<div class="ds-editorial-drawer__backdrop" data-ds-drawer-close></div>' +
      '<aside class="ds-editorial-drawer__panel" role="dialog" aria-modal="true" aria-label="Product option">' +
      '<header class="ds-editorial-drawer__header">' +
      '<h2 class="ds-editorial-drawer__title"></h2>' +
      '<button type="button" class="ds-editorial-drawer__close" data-ds-drawer-close aria-label="Close">' +
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
      '<path d="M3.5 3.5L14.5 14.5M14.5 3.5L3.5 14.5" stroke="currentColor" stroke-width="1.3"/>' +
      '</svg></button></header>' +
      '<div class="ds-editorial-drawer__scroll"><div class="ds-editorial-drawer__options"></div></div>' +
      '</aside>';
    document.body.appendChild(root);
    const panel = root.querySelector('.ds-editorial-drawer__panel');
    const header = root.querySelector('.ds-editorial-drawer__title');
    const closeButton = root.querySelector('.ds-editorial-drawer__close');
    const options = root.querySelector('.ds-editorial-drawer__options');
    const locale = (document.documentElement.lang || 'de').toLowerCase();
    const closeLabel = locale.startsWith('de') ? 'Schließen' :
      locale.startsWith('fr') ? 'Fermer' : locale.startsWith('es') ? 'Cerrar' :
      locale.startsWith('it') ? 'Chiudi' : locale.startsWith('nl') ? 'Sluiten' :
      locale.startsWith('da') ? 'Luk' : locale.startsWith('no') ? 'Lukk' :
      locale.startsWith('sv') ? 'Stäng' : 'Close';
    closeButton.setAttribute('aria-label', closeLabel);

    let type = null;
    let lastFocus = null;
    let closingTimer = null;
    let paintId = null;

    function appendThumbnail(tile, url, empty) {
      const thumb = document.createElement('span');
      thumb.className = 'ds-editorial-drawer__thumb';
      if (empty) thumb.classList.add('is-empty');
      if (url && !empty) {
        const img = document.createElement('img');
        img.src = url;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 200;
        img.height = 200;
        thumb.appendChild(img);
      }
      tile.appendChild(thumb);
    }
    function labelFromSource(source, mode) {
      const label = source.querySelector(mode === 'size' ? '.custom-size-option__left' : '.custom-frame-card__title');
      return (label ? label.textContent : source.textContent || '').trim();
    }
    function clonePrice(source, mode, tile) {
      const outer = document.createElement('span');
      outer.className = 'ds-editorial-drawer__price';
      if (mode === 'size') {
        const old = source.querySelector('.custom-size-option__right');
        if (old) outer.appendChild(old.cloneNode(true));
      } else {
        const group = source.querySelector('.deinspar-frame-price-group');
        const simplePrice = source.querySelector('.custom-frame-card__price');
        if (group) outer.appendChild(group.cloneNode(true));
        else if (simplePrice && simplePrice.textContent.trim() !== '—') outer.appendChild(simplePrice.cloneNode(true));
      }
      if (outer.textContent.trim()) tile.appendChild(outer);
    }
    function buildOptions(mode) {
      options.replaceChildren();
      // main-product.js MOVES the frame dropdown into its mobile sheet.
      // Resolve its buttons from the document, not only from the picker.
      const sources = mode === 'size'
        ? [...picker.querySelectorAll('.custom-size-option[data-custom-size-value]')]
        : [...document.querySelectorAll('[data-custom-frame-dropdown] [data-custom-frame-value]')];
      const posterUrl = picker.dataset.deinsparPosterImage || '';
      sources.forEach(source => {
        if (source.hidden) return;
        const unavailable = source.disabled || source.classList.contains('is-disabled');
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'ds-editorial-drawer__option';
        tile.disabled = unavailable;
        tile.classList.toggle('is-selected', source.classList.contains('is-active'));
        tile.setAttribute('aria-pressed', source.classList.contains('is-active') ? 'true' : 'false');
        if (mode === 'size') {
          // Desktop: familiar image tiles. Mobile: text-only rows.
          if (!mobile.matches) appendThumbnail(tile, posterUrl, !posterUrl);
        } else {
          const key = source.dataset.deinsparFrameKey || 'none';
          const frameUrl = getFramePreviewUrl(key);
          appendThumbnail(tile, frameUrl, key === 'none');
        }
        const name = document.createElement('span');
        name.className = 'ds-editorial-drawer__option-name';
        name.textContent = labelFromSource(source, mode);
        tile.appendChild(name);
        clonePrice(source, mode, tile);
        tile.addEventListener('click', () => {
          if (unavailable) return;
          // The original button owns native variant selection, pricing, overlays,
          // and the real poster + set-frame cart logic. Reuse it unchanged.
          source.click();
          hideDrawer(true);
        });
        options.appendChild(tile);
      });
    }
    function showDrawer(mode) {
      // Safari resizes its visual viewport when browser chrome changes.
      // Never restart an already opening/open drawer: that used to cancel
      // its pending animation and could leave the panel halfway in view.
      if (type === mode && !root.hidden) return;
      const nextTitle = mode === 'size' ?
        (picker.dataset.deinsparPosterHeading || 'Poster') :
        (picker.dataset.deinsparFrameHeading || 'Bilderrahmen');
      if (type !== mode) lastFocus = document.activeElement;
      type = mode;
      if (closingTimer !== null) { clearTimeout(closingTimer); closingTimer = null; }
      root.hidden = false;
      root.classList.toggle('is-size', mode === 'size');
      root.classList.toggle('is-frame', mode === 'frame');
      header.textContent = nextTitle;
      panel.setAttribute('aria-label', nextTitle);
      buildOptions(mode);
      document.body.classList.add('ds-editorial-drawer-open');
      cancelAnimationFrame(paintId);
      paintId = requestAnimationFrame(() => {
        paintId = requestAnimationFrame(() => {
          root.classList.add('is-visible');
          paintId = null;
          closeButton.focus({ preventScroll: true });
        });
      });
    }
    function hideDrawer(restoreFocus) {
      if (type === null && root.hidden) return;
      const wasOpen = type !== null;
      type = null;
      root.classList.remove('is-visible');
      document.body.classList.remove('ds-editorial-drawer-open');
      cancelAnimationFrame(paintId);
      if (closingTimer !== null) clearTimeout(closingTimer);
      closingTimer = setTimeout(() => {
        if (type === null) root.hidden = true;
        closingTimer = null;
      }, 400);
      if (restoreFocus && wasOpen) {
        const focus = lastFocus?.isConnected ? lastFocus : sizeTrigger;
        focus.focus({ preventScroll: true });
      }
    }
    function requestClose() {
      const trigger = type === 'size' ? sizeTrigger : type === 'frame' ? frameTrigger : null;
      // Let the original main-product.js close its own state.
      if (trigger?.getAttribute('aria-expanded') === 'true') trigger.click();
      hideDrawer(true);
    }
    root.querySelectorAll('[data-ds-drawer-close]').forEach(el => el.addEventListener('click', requestClose));
    root.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
      } else if (event.key === 'Tab' && type !== null) {
        const nodes = [...panel.querySelectorAll('button:not(:disabled)')];
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }
    });
    function sync() {
      if (sizePicker.classList.contains('is-open')) showDrawer('size');
      else if (framePicker.classList.contains('is-open')) showDrawer('frame');
      else hideDrawer(false);
    }
    // The original JS toggles is-open on these exact elements. Observe that
    // instead of intercepting or duplicating its click/selection handlers.
    const observer = new MutationObserver(sync);
    observer.observe(sizePicker, { attributes: true, attributeFilter: ['class'] });
    observer.observe(framePicker, { attributes: true, attributeFilter: ['class'] });
    // Rebuild only when switching between desktop and mobile layouts.
    // Ordinary Safari address-bar/viewport resize events are NOT open/close
    // events and should never restart the animation or change header state.
    function onViewportModeChange() {
      if (type !== null && !root.hidden) buildOptions(type);
    }
    if (typeof mobile.addEventListener === 'function') {
      mobile.addEventListener('change', onViewportModeChange);
    } else if (typeof mobile.addListener === 'function') {
      mobile.addListener(onViewportModeChange);
    }
  }

  function initPicker(picker) {
    if (picker.dataset.deinsparVisualReady === 'true') return;
    const sizeButton = picker.querySelector('[data-custom-size-trigger]');
    const sizeText = picker.querySelector('[data-custom-size-trigger-text]');
    const frameButton = picker.querySelector('[data-custom-frame-trigger]');
    const frameText = picker.querySelector('[data-custom-frame-trigger-text]');
    if (!sizeButton || !sizeText || !frameButton || !frameText) return;
    picker.dataset.deinsparVisualReady = 'true';

    function square(src, modifier) {
      const square = document.createElement('span');
      square.className = 'deinspar-picker-thumb deinspar-picker-thumb--' + modifier;
      square.setAttribute('aria-hidden', 'true');
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.width = 56;
        img.height = 56;
        img.decoding = 'async';
        square.appendChild(img);
      }
      return square;
    }
    function labelText(text) {
      const el = document.createElement('span');
      el.className = 'deinspar-picker-heading';
      el.textContent = text;
      return el;
    }
    function wrapLabel(valueElement, heading) {
      const copy = document.createElement('span');
      copy.className = 'deinspar-picker-copy';
      valueElement.before(copy);
      copy.appendChild(labelText(heading));
      copy.appendChild(valueElement);
    }

    // Poster artwork is taken from the real Shopify product featured image.
    sizeButton.prepend(square(picker.dataset.deinsparPosterImage || '', 'poster'));
    wrapLabel(sizeText, picker.dataset.deinsparPosterHeading || 'Poster');

    // Frame preview is a separate thumbnail, rather than the old 17px swatch.
    const frameSquare = square('', 'frame');
    frameButton.prepend(frameSquare);
    wrapLabel(frameText, picker.dataset.deinsparFrameHeading || 'Bilderrahmen');

    // Presentation only: do not change the chosen frame variant, overlay or price.
    let frameImages = {};
    let setFrameImages = {};
    let rectangleAssets = {};
    let useRectangleAssets = false;
    try {
      const json = document.querySelector('[data-deinspar-frame-data]');
      if (json) {
        const data = JSON.parse(json.textContent);
        frameImages = data.frames || {};
        setFrameImages = data.setFrames || {};
        rectangleAssets = data.pickerPreview?.rectangleAssets || {};
        useRectangleAssets = data.pickerPreview?.isRectangle === true;
      }
    } catch (error) {
      console.warn('[Deinspar] Rahmenbilder konnten nicht gelesen werden.', error);
    }

    function normalizeSize(value) {
      const match = String(value || '').replace(/[×✕]/g, 'x').match(/(\d{2,3})\s*x\s*(\d{2,3})/i);
      return match ? `${Number(match[1])}x${Number(match[2])}` : '';
    }

    function currentSize() {
      const selected = picker.querySelector('.custom-size-option.is-active[data-custom-size-value]') ||
        picker.querySelector('.custom-size-sheet-option.is-active[data-custom-size-value]');
      return normalizeSize(selected?.getAttribute('data-custom-size-value') || sizeText.textContent);
    }

    function baseFrameImage(key) {
      return String(frameImages[key]?.image || setFrameImages[key]?.image || '');
    }

    function getFramePreviewUrl(key) {
      if (!key || key === 'none') return '';

      // 50x50: an image assigned to the matching Shopify frame variant wins.
      // Use the standard frame product before the set-frame product.
      if (currentSize() === '50x50') {
        for (const entry of [frameImages[key], setFrameImages[key]]) {
          const match = (entry?.variants || []).find(variant =>
            normalizeSize(variant.size) === '50x50' && variant.pickerImage
          );
          if (match?.pickerImage) return String(match.pickerImage);
        }
        return baseFrameImage(key);
      }

      // Rectangle-tagged posters use the uploaded Theme Assets exclusively
      // for the picker illustration, never for the overlay or cart image.
      if (useRectangleAssets && rectangleAssets[key]) {
        return String(rectangleAssets[key]);
      }
      return baseFrameImage(key);
    }

    // IMPORTANT: only react to the SAME active color that existing
    // main-product.js uses for adding the real set-frame variant.
    function currentColor() {
      const cards = picker.querySelectorAll('[data-custom-frame-value]');
      const active = Array.from(cards).find(card => card.classList.contains('is-active'));
      if (active) return active.dataset.deinsparFrameKey || 'none';
      // On mobile main-product.js moves the dropdown into the bottom sheet.
      const mobileActive = document.querySelector('[data-custom-frame-sheet] [data-custom-frame-value].is-active');
      if (mobileActive) return mobileActive.dataset.deinsparFrameKey || 'none';
      return 'none';
    }

    let lastKey = null;
    let lastSrc = null;
    function syncFrameImage() {
      const key = currentColor();
      const url = getFramePreviewUrl(key);
      if (lastKey === key && lastSrc === url) return;
      lastKey = key;
      lastSrc = url;
      // No-frame: square is deliberately completely empty, no icon or image.
      frameSquare.replaceChildren();
      if (!url) return;
      const img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.decoding = 'async';
      frameSquare.appendChild(img);
    }

    let queued = false;
    function queueSync() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; syncFrameImage(); });
    }
    // Main-product.js updates this exact value when a color is selected.
    new MutationObserver(queueSync).observe(frameText, {
      childList: true, subtree: true, characterData: true
    });
    // Size changes can change the picture even while the frame color stays the same.
    new MutationObserver(queueSync).observe(sizeText, {
      childList: true, subtree: true, characterData: true
    });
    picker.addEventListener('click', event => {
      if (event.target.closest('[data-custom-frame-value], [data-custom-size-value]')) queueSync();
    });
    document.addEventListener('click', event => {
      if (event.target.closest('[data-custom-frame-sheet] [data-custom-frame-value]')) queueSync();
    });
    document.addEventListener('variant:change', queueSync);
    syncFrameImage();
    initResponsiveDrawer(picker, getFramePreviewUrl);
  }

  function boot() {
    document.querySelectorAll('[data-deinspar-visual-picker]').forEach(initPicker);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else boot();
  document.addEventListener('shopify:section:load', boot);
})();
